import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Send, X } from 'lucide-react';
import { useAnthropicChat } from '../../hooks/useAnthropicChat';
import { supabase } from '../../lib/supabase';
import './Participant.css';

const TOTAL_QUESTIONS = 7;

function TypingIndicator() {
  return (
    <div className="msg msg-ai">
      <div className="typing-dots"><span /><span /><span /></div>
    </div>
  );
}

export default function InterviewPage() {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [session, setSession] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [questionCount, setQuestionCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [started, setStarted] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // جلب كل شيء من Supabase مباشرة — مستقل عن Context
  useEffect(() => {
    const load = async () => {
      try {
        const [agentRes, sessionRes, settingsRes] = await Promise.all([
          supabase.from('agents').select('*').eq('id', agentId).single(),
          sessionId ? supabase.from('sessions').select('*').eq('id', sessionId).single() : Promise.resolve({ data: null }),
          supabase.from('settings').select('value').eq('key', 'gemini_api_key').single(),
        ]);

        if (agentRes.data) {
          setAgent({
            ...agentRes.data,
            questionCount: agentRes.data.question_count,
            outputFields: agentRes.data.output_fields,
          });
        } else {
          setLoadError(true);
        }

        if (sessionRes.data) setSession(sessionRes.data);

        // API key من Supabase أو localStorage كـ fallback
        const key = settingsRes.data?.value || localStorage.getItem('ux_api_key') || '';
        setApiKey(key);
      } catch (e) {
        console.error('Load error:', e);
        setLoadError(true);
      }
    };
    load();
  }, [agentId, sessionId]);

  // تمرير apiKey مباشرة للـ hook
  const { sendMessage, analyzeInterview, getInitialMessage } = useAnthropicChat(agent, apiKey);

  // بدء المقابلة بعد تحميل الوكيل والمفتاح
  useEffect(() => {
    if (agent && apiKey && !started) {
      setStarted(true);
      setIsTyping(true);
      getInitialMessage().then(msg => {
        setMessages([{ role: 'assistant', content: msg }]);
        setQuestionCount(1);
        setIsTyping(false);
      });
    }
  }, [agent, apiKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // حفظ مباشر في Supabase
  const saveToSupabase = async (id, updates) => {
    const { error } = await supabase.from('sessions').update(updates).eq('id', id);
    if (error) console.error('Supabase save error:', error);
    else console.log('Saved to Supabase:', Object.keys(updates));
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping || analyzing) return;
    const userMsg = input.trim();
    setInput('');
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setIsTyping(true);

    if (questionCount >= TOTAL_QUESTIONS) {
      await handleEndInterview(newMessages);
      return;
    }

    const aiResponse = await sendMessage(userMsg, messages);
    if (aiResponse) {
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      setQuestionCount(c => c + 1);
    }
    setIsTyping(false);
  };

  const handleEndInterview = async (finalMessages) => {
    const msgs = finalMessages || messages;
    const startTime = session?.created_at ? new Date(session.created_at) : new Date();
    const durationMinutes = Math.round((new Date() - startTime) / 60000);

    setIsTyping(false);

    // ١. حفظ الرسائل فوراً
    await saveToSupabase(sessionId, {
      status: 'completed',
      messages: msgs,
      duration_minutes: durationMinutes,
    });

    // ٢. رسالة شكر
    setMessages(prev => [...prev, { role: 'assistant', content: 'شكراً جزيلاً على مشاركتك! 🎉 إجاباتك وصلتنا بنجاح.' }]);
    setAnalyzing(true);

    // ٣. التحليل
    const analysis = await analyzeInterview(msgs);
    if (analysis) {
      await saveToSupabase(sessionId, { analysis });
      console.log('Analysis saved successfully');
    } else {
      console.error('Analysis returned null — check API key and messages');
    }

    setAnalyzing(false);
    navigate(`/interview/${agentId}/done?session=${sessionId}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const progress = Math.min((questionCount / TOTAL_QUESTIONS) * 100, 100);

  if (loadError) return (
    <div className="participant-bg">
      <div className="p-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h2 style={{ color: 'var(--brown)' }}>رابط المقابلة غير صالح</h2>
        <p style={{ color: 'var(--brown-muted)' }}>يبدو أن هذا الرابط منتهي أو غير صحيح</p>
      </div>
    </div>
  );

  if (!agent) return (
    <div className="participant-bg">
      <div className="p-card" style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ margin: '0 auto 1rem' }} />
        <p style={{ color: 'var(--brown-muted)' }}>جاري التحميل...</p>
      </div>
    </div>
  );

  return (
    <div className="chat-bg">
      <div className="chat-container">
        <div className="chat-header">
          <div className="chat-agent-info">
            <div className="chat-avatar">ن</div>
            <div>
              <div className="chat-agent-name">نور – مساعدة أبحاث المستخدم</div>
              <div className="chat-agent-status">
                {analyzing ? '● جاري تحليل إجاباتك...' : isTyping ? '● يكتب...' : '● متصل'}
              </div>
            </div>
          </div>
          <button className="chat-end-btn" onClick={() => setShowEndConfirm(true)} disabled={analyzing || isTyping}>
            <X size={14} />إنهاء
          </button>
        </div>

        <div className="chat-progress">
          <div className="progress-info">
            <span>التقدم</span>
            <span>سؤال {Math.min(questionCount, TOTAL_QUESTIONS)} من {TOTAL_QUESTIONS}</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`msg ${msg.role === 'assistant' ? 'msg-ai' : 'msg-user'} animate-in`}>
              {msg.content}
            </div>
          ))}
          {(isTyping || analyzing) && <TypingIndicator />}
          <div ref={bottomRef} />
        </div>

        <div className="chat-input-bar">
          <textarea
            ref={inputRef}
            className="chat-textarea"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={analyzing ? 'جاري التحليل...' : questionCount >= TOTAL_QUESTIONS ? 'اضغط إرسال لإنهاء المقابلة...' : 'اكتب ردك هنا...'}
            rows={1}
            disabled={isTyping || analyzing}
          />
          <button className="chat-send-btn" onClick={handleSend} disabled={!input.trim() || isTyping || analyzing}>
            <Send size={16} />
          </button>
        </div>
      </div>

      {showEndConfirm && (
        <div className="modal-overlay" onClick={() => setShowEndConfirm(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--brown)', marginBottom: '0.75rem' }}>إنهاء المقابلة؟</h3>
            <p style={{ color: 'var(--brown-muted)', fontSize: '14px', marginBottom: '1.25rem' }}>سيتم حفظ إجاباتك حتى الآن.</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setShowEndConfirm(false); handleEndInterview(); }}>نعم، إنهاء</button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowEndConfirm(false)}>تراجع</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
